import { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Check, X, Star, ArrowRight } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { useIsMobile } from "@/hooks/use-mobile";
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from "@/components/ui/carousel";
import { StructuredComparisonTable } from "@/components/comparison/StructuredComparisonTable";
import { LegacyComparisonGrid } from "@/components/comparison/LegacyComparisonGrid";
import { Product } from "@/types/product";
import { isFeatureEnabled } from "@/lib/featureFlags";

const Compare = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [insights, setInsights] = useState<string>("");
  const [loadingInsights, setLoadingInsights] = useState(false);
  const isMobile = useIsMobile();

  useEffect(() => {
    const fetchProducts = async () => {
      const ids = searchParams.get("ids");
      
      if (!ids) {
        toast.error("No products selected for comparison");
        navigate("/browse");
        return;
      }

      const slugArray = ids.split(",").filter(Boolean);
      
      // Defensive check for UUIDs vs slugs for backward compatibility
      const isUUID = (str: string) => /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(str);
      const firstParam = slugArray[0];
      const queryField = firstParam && isUUID(firstParam) ? "id" : "slug";
      
      if (slugArray.length < 2) {
        toast.error("Please select at least 2 products to compare");
        navigate("/browse");
        return;
      }

      if (slugArray.length > 4) {
        toast.error("Maximum 4 products can be compared at once");
        return;
      }

      try {
        // Fetch enhanced product data including Circle Score and best_for
        const { data, error } = await supabase
          .from("products")
          .select(`
            id,
            slug,
            title,
            subtitle,
            image,
            price,
            price_duration,
            rating,
            review_count,
            categories,
            tags,
            features,
            service_type,
            best_for,
            is_select,
            circle_score,
            score_popularity,
            score_value,
            score_ease_of_use,
            score_support,
            score_roi,
            score_calculated_at,
            product_plans(price)
          `)
          .in(queryField, slugArray)
          .eq("is_published", true);

        if (error) throw error;

        if (data && data.length > 0) {
          // Transform to Product type
          const transformedProducts = data.map(product => {
            const circleScore = product.circle_score ? {
              circle_score: product.circle_score,
              score_popularity: product.score_popularity,
              score_value: product.score_value,
              score_ease_of_use: product.score_ease_of_use,
              score_support: product.score_support,
              score_roi: product.score_roi,
              score_calculated_at: product.score_calculated_at,
            } : null;

            return {
              ...product,
              circleScore,
              plansCount: product.product_plans?.length || 0,
              startingPrice: product.product_plans?.length 
                ? Math.min(...product.product_plans.map((p: any) => p.price || 0).filter((p: number) => p > 0))
                : product.price,
              categories: Array.isArray(product.categories) ? product.categories : [],
              tags: Array.isArray(product.tags) ? product.tags : [],
              best_for: Array.isArray(product.best_for) ? product.best_for : [],
            };
          }) as unknown as Product[];

          setProducts(transformedProducts);
          // Fetch AI insights after products are loaded
          fetchInsights(transformedProducts);
        } else {
          toast.error("Products not found");
          navigate("/browse");
        }
      } catch (error) {
        console.error("Error fetching products:", error);
        toast.error("Failed to load products for comparison");
      } finally {
        setLoading(false);
      }
    };

    fetchProducts();
  }, [searchParams, navigate]);

  const fetchInsights = async (productsData: Product[]) => {
    setLoadingInsights(true);
    try {
      const { data, error } = await supabase.functions.invoke('generate-comparison-insights', {
        body: { products: productsData }
      });

      if (error) throw error;
      if (data?.insights) {
        setInsights(data.insights);
      }
    } catch (error) {
      console.error("Error fetching insights:", error);
      toast.error("Failed to generate AI insights");
    } finally {
      setLoadingInsights(false);
    }
  };

  const extractFeatures = (product: Product) => {
    if (!product.features) return [];
    
    if (Array.isArray(product.features)) {
      return product.features.map((f: any) => {
        if (typeof f === 'string') return f;
        if (f.title) return f.title;
        return '';
      }).filter(Boolean);
    }
    
    return [];
  };

  const getAllFeatures = () => {
    const featureSet = new Set<string>();
    products.forEach(product => {
      extractFeatures(product).forEach(feature => featureSet.add(feature));
    });
    return Array.from(featureSet);
  };

  const hasFeature = (product: Product, featureName: string) => {
    const features = extractFeatures(product);
    return features.some(f => f.toLowerCase().includes(featureName.toLowerCase()) || 
                              featureName.toLowerCase().includes(f.toLowerCase()));
  };

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col">
        <Header />
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
            <p className="text-muted-foreground">Loading comparison...</p>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  if (products.length === 0) {
    return (
      <div className="min-h-screen flex flex-col">
        <Header />
        <div className="flex-1 flex items-center justify-center p-8">
          <div className="text-center max-w-md">
            <h2 className="text-2xl font-bold mb-4">No Products to Compare</h2>
            <p className="text-muted-foreground mb-6">
              Select products from the browse page or your favorites to compare them side-by-side.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button onClick={() => navigate("/browse")}>
                Browse Products
              </Button>
              <Button 
                variant="outline"
                onClick={() => navigate("/ask-circle")}
                className="border-emerald-600 text-emerald-700 hover:bg-emerald-50"
              >
                <span className="flex items-baseline gap-1">
                  <span>Get recommendations from Ask</span>
                  <span className="font-handelson font-light text-base -ml-0.5">Circle</span>
                </span>
              </Button>
            </div>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  // Mobile Carousel View
  const renderMobileView = () => {
    const allFeatures = getAllFeatures();
    
    return (
      <div className="min-h-screen flex flex-col bg-background">
        <Header />
        <main className="flex-1 container mx-auto px-4 py-6">
          <div className="mb-6">
            <div className="flex items-center gap-3 mb-2">
              <Button 
                variant="ghost" 
                size="sm"
                onClick={() => navigate(-1)}
                className="p-2 h-auto"
              >
                ← Back
              </Button>
              <h1 className="text-2xl font-bold flex-1">Compare Products</h1>
            </div>
            <p className="text-sm text-muted-foreground">{products.length} products • Swipe to compare</p>
          </div>

          <Carousel className="w-full">
            <CarouselContent>
              {products.map((product) => (
                <CarouselItem key={product.id}>
                  <Card className="border-2">
                    <CardHeader className="pb-3">
                      <div className="relative w-full h-48 mb-4 overflow-hidden rounded-lg bg-muted">
                        {product.image && (
                          <img 
                            src={product.image} 
                            alt={product.title}
                            className="w-full h-full object-cover"
                          />
                        )}
                      </div>
                      <CardTitle className="text-xl">{product.title}</CardTitle>
                      {product.subtitle && (
                        <p className="text-sm text-muted-foreground mt-1">{product.subtitle}</p>
                      )}
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div className="flex items-baseline gap-2 pb-3 border-b">
                        <span className="text-3xl font-bold">
                          ${product.price?.toFixed(0) || "N/A"}
                        </span>
                        {product.price_duration && (
                          <span className="text-sm text-muted-foreground">
                            /{product.price_duration}
                          </span>
                        )}
                      </div>
                      
                      {product.rating && (
                        <div className="flex items-center gap-1">
                          <Star className="h-5 w-5 fill-yellow-400 text-yellow-400" />
                          <span className="font-medium text-lg">{product.rating.toFixed(1)}</span>
                        </div>
                      )}

                      {product.categories && Array.isArray(product.categories) && product.categories.length > 0 && (
                        <div className="flex flex-wrap gap-2">
                          {product.categories.slice(0, 3).map((cat: string, i: number) => (
                            <Badge key={i} variant="secondary">
                              {cat}
                            </Badge>
                          ))}
                        </div>
                      )}

                      <div className="space-y-3 pt-4 border-t">
                        <h3 className="font-semibold text-lg">Features</h3>
                        {allFeatures.map((feature, idx) => (
                          <div key={idx} className="flex items-start gap-3 py-2">
                            {hasFeature(product, feature) ? (
                              <Check className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                            ) : (
                              <X className="h-5 w-5 text-muted-foreground mt-0.5 flex-shrink-0" />
                            )}
                            <span className="text-sm flex-1">{feature}</span>
                          </div>
                        ))}
                      </div>

                      <Button 
                        className="w-full mt-4" 
                        size="lg"
                        onClick={() => navigate(`/product/${product.slug}`)}
                      >
                        View Full Details
                        <ArrowRight className="ml-2 h-4 w-4" />
                      </Button>
                    </CardContent>
                  </Card>
                </CarouselItem>
              ))}
            </CarouselContent>
            <CarouselPrevious className="left-2" />
          <CarouselNext className="right-2" />
        </Carousel>

        {/* AI Insights Section */}
        {loadingInsights && (
          <Card className="mt-6">
            <CardContent className="p-6">
              <div className="flex items-center justify-center gap-3">
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-primary"></div>
                <p className="text-muted-foreground">Generating AI insights...</p>
              </div>
            </CardContent>
          </Card>
        )}

        {insights && !loadingInsights && (
          <Card className="mt-6">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Star className="h-5 w-5 text-yellow-500" />
                AI Comparison Insights
              </CardTitle>
            </CardHeader>
            <CardContent className="prose prose-sm max-w-none">
              <div className="whitespace-pre-wrap text-sm leading-relaxed">
                {insights}
              </div>
            </CardContent>
          </Card>
        )}

        <div className="mt-6 text-center">
          <Button 
            variant="outline" 
            onClick={() => navigate("/browse")}
            className="w-full sm:w-auto"
          >
            Browse More Products
          </Button>
        </div>
      </main>
      <Footer />
    </div>
  );
  };

  // Desktop Grid View
  const renderDesktopView = () => {
    const useStructuredTable = isFeatureEnabled('circleScore') || isFeatureEnabled('promoCodes');
    
    return (
      <div className="min-h-screen flex flex-col">
        <Header />
        
        <main className="flex-1 container mx-auto px-4 py-8">
          <div className="mb-8">
            <Button 
              variant="outline" 
              onClick={() => navigate(-1)}
              className="mb-4"
            >
              ← Back
            </Button>
            
            <h1 className="text-3xl md:text-4xl font-bold mb-2">Compare Products</h1>
            <p className="text-muted-foreground">
              Side-by-side comparison of {products.length} products
            </p>
          </div>

          {useStructuredTable ? (
            <div className="overflow-x-auto">
              <StructuredComparisonTable products={products} />
            </div>
          ) : (
            <LegacyComparisonGrid products={products} />
          )}

        {/* AI Insights Section */}
        {loadingInsights && (
          <Card className="mt-8">
            <CardContent className="p-8">
              <div className="flex items-center justify-center gap-3">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
                <p className="text-muted-foreground">Generating AI insights...</p>
              </div>
            </CardContent>
          </Card>
        )}

        {insights && !loadingInsights && (
          <Card className="mt-8">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-2xl">
                <Star className="h-6 w-6 text-yellow-500" />
                AI Comparison Insights
              </CardTitle>
            </CardHeader>
            <CardContent className="prose prose-base max-w-none">
              <div className="whitespace-pre-wrap leading-relaxed">
                {insights}
              </div>
            </CardContent>
          </Card>
        )}

        <div className="mt-8 text-center">
          <Button 
            variant="outline"
            onClick={() => navigate("/browse")}
          >
            Browse More Products
          </Button>
        </div>
      </main>

      <Footer />
    </div>
  );
  };

  return isMobile ? renderMobileView() : renderDesktopView();
};

export default Compare;
